import { Dialog } from '@llt-frontend/react-material-dialog';
import PropTypes from 'prop-types';
import React from 'react';
import makeRequest from '@llt-frontend/make-request';
import { useInteractionTracker } from '@blueshift-ui/telemetry';
import { useSnackbarContext } from '@llt-frontend/react-material-snackbar';
import useStudentSubjects from '../../hooks/data-access/use-student-subjects';
import { useTranslation } from 'react-i18next';
import useUserContext from '../../hooks/context/use-user-context';

//

function SubjectDeletionConfirmation({
  closeConfirmation,
  confirmationOpen,
  onDelete,
  onDeleting,
  onError,
  subjectId,
}) {
  const { t: translate, ready: translationLoaded } = useTranslation('subjects/delete');

  const { activeStudentId } = useUserContext();
  const snackbar = useSnackbarContext();

  const { refetch: refetchSubjects } = useStudentSubjects({
    studentId: activeStudentId,
  });

  const { trackEvent } = useInteractionTracker();

  //
  // Methods

  async function deleteSubject() {
    const response = await makeRequest(
      `${process.env.REACT_APP_PROGRESS_HOST}/v1/learn-lab/progress/students/${activeStudentId}/subjects/${subjectId}`,
      {
        method: 'DELETE',
      }
    );

    const deleteSucceeded = response !== undefined;

    snackbar.setMessage({
      icon: 'delete_forever',
      text: translate(deleteSucceeded ? 'subjectDeleted' : 'unableToDelete'),
    });

    if (deleteSucceeded) {
      await refetchSubjects();
      onDelete && onDelete(subjectId);
    } else {
      onError && onError(subjectId);
    }
  }

  //
  // Event Handlers

  async function handleConfirmation() {
    onDeleting && onDeleting(subjectId);
    await deleteSubject();
  }

  function handleDeleteCancel() {
    trackEvent({
      action: 'cancel-delete-subject',
      category: 'side-nav',
    });
  }

  if (!translationLoaded) {
    return null;
  }

  return (
    <Dialog
      actions={{
        close: {
          label: translate('cancel'),
          eventHandler: handleDeleteCancel,
        },
        confirm: {
          eventHandler: handleConfirmation,
          label: translate('okay'),
        },
      }}
      id={`remove-${subjectId}-subject-confirmation`}
      onClose={closeConfirmation}
      open={confirmationOpen}
      title={translate('deleteSubject')}
    >
      {translate('deleteSubjectWarning')}
    </Dialog>
  );
}

SubjectDeletionConfirmation.propTypes = {
  closeConfirmation: PropTypes.func.isRequired,
  confirmationOpen: PropTypes.bool,
  onDelete: PropTypes.func,
  onDeleting: PropTypes.func,
  onError: PropTypes.func,
  subjectId: PropTypes.string.isRequired,
};

// Exports
// -------

export default SubjectDeletionConfirmation;
